import React from 'react';
import { Building2, CheckCircle, ArrowRight, AlertTriangle, Target, Mail, Clock } from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const ClientInformationDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Client Information"
        description="Manage your organization's profile, contact details, notification settings, and branding to ensure accurate communication and professional presentation across the CRM."
        icon={<Building2 className="w-6 h-6" />}
        badge="Settings"
        badgeColor="purple"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* What You'll Learn */}
      <div className="not-prose bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Target className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-200 mb-3">
              What You'll Learn
            </h3>
            <ul className="space-y-1 text-sm text-purple-800 dark:text-purple-300">
              <li>✓ Managing company information and branding</li>
              <li>✓ Configuring notification email with AWS SES verification</li>
              <li>✓ Setting up timezone for accurate timestamps</li>
              <li>✓ Adding complete address information</li>
              <li>✓ Uploading company logo via URL</li>
              <li>✓ Understanding email verification requirements</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>What is Client Information?</h2>
      <p>
        Client Information is your organization's profile in the CRM system. It contains essential
        details about your company that are used throughout the system for:
      </p>
      <ul>
        <li><strong>Email Communications</strong> - Your notification email receives system alerts</li>
        <li><strong>Timestamp Accuracy</strong> - Correct timezone ensures accurate activity logs</li>
        <li><strong>Professional Branding</strong> - Company logo appears in reports and communications</li>
        <li><strong>Contact Details</strong> - Used in email signatures and customer communications</li>
        <li><strong>Compliance</strong> - Proper address information for legal requirements</li>
      </ul>

      <h2>Accessing Client Information</h2>
      <p>
        Navigate to <code>Settings → System Configuration → Client Information</code> (Admin only)
      </p>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              <strong>⚠️ Admin Access Required:</strong>
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Only users with admin privileges can view and edit client information. This ensures
              that critical company details remain secure and consistent across the organization.
            </p>
          </div>
        </div>
      </div>

      <h2>Company Information Section</h2>

      <h3>Company Name</h3>
      <p>
        Your organization's official name as it appears in the CRM system. This name is used in:
      </p>
      <ul>
        <li>Email templates and signatures</li>
        <li>Report headers and footers</li>
        <li>Customer-facing communications</li>
        <li>System branding elements</li>
      </ul>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Best Practice:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Use your official registered company name for consistency with legal documents and official communications.
        </p>
      </div>

      <h3>Website</h3>
      <p>
        Your company's official website URL. Include the full URL with protocol:
      </p>
      <ul>
        <li>✅ Correct: <code>https://example.com</code></li>
        <li>❌ Incorrect: <code>example.com</code> or <code>www.example.com</code></li>
      </ul>

      <h3>Timezone</h3>
      <p>
        Select your organization's primary timezone. This ensures:
      </p>
      <ul>
        <li>Activity timestamps display in your local time</li>
        <li>Scheduled tasks run at correct times</li>
        <li>Reports show accurate date/time information</li>
        <li>Drip campaign emails send at intended times</li>
      </ul>

      <div className="not-prose my-6">
        <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Available Timezones:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-700 rounded">
            <Clock className="w-4 h-4 inline mr-2 text-gray-400" />
            Asia/Kolkata (IST)
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-700 rounded">
            <Clock className="w-4 h-4 inline mr-2 text-gray-400" />
            America/New_York (EST)
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-700 rounded">
            <Clock className="w-4 h-4 inline mr-2 text-gray-400" />
            America/Los_Angeles (PST)
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-700 rounded">
            <Clock className="w-4 h-4 inline mr-2 text-gray-400" />
            Europe/London (GMT)
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-700 rounded">
            <Clock className="w-4 h-4 inline mr-2 text-gray-400" />
            Asia/Dubai (GST)
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300 p-2 bg-gray-50 dark:bg-gray-700 rounded">
            <Clock className="w-4 h-4 inline mr-2 text-gray-400" />
            Australia/Sydney (AEDT)
          </div>
        </div>
      </div>

      <h3>Company Logo URL</h3>
      <p>
        Add your company logo by providing a publicly accessible image URL. The logo will be displayed in:
      </p>
      <ul>
        <li>Navigation header</li>
        <li>Email templates</li>
        <li>PDF reports</li>
        <li>Client-facing documents</li>
      </ul>

      <h4>How to Add a Logo:</h4>
      <ol>
        <li>Upload your logo to a public hosting service (e.g., your website, Imgur, Cloudinary)</li>
        <li>Copy the direct image URL (must end with .png, .jpg, or .svg)</li>
        <li>Paste the URL in the "Company Logo URL" field</li>
        <li>Preview appears automatically on the right</li>
        <li>Click "Save Changes" to apply</li>
      </ol>

      <div className="not-prose bg-green-50 dark:bg-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 my-6">
        <p className="text-sm text-green-800 dark:text-green-300 mb-2">
          <strong>📏 Logo Requirements:</strong>
        </p>
        <ul className="text-sm text-brandColor dark:text-green-300 space-y-1">
          <li>• Format: PNG, JPG, or SVG</li>
          <li>• Recommended size: 200x200px minimum</li>
          <li>• Transparent background recommended</li>
          <li>• Square or horizontal orientation works best</li>
        </ul>
      </div>

      <h3>Description</h3>
      <p>
        Brief description of your company (2-3 sentences). This may appear in reports and system-generated documents.
      </p>

      <h2>Contact Information Section</h2>

      <h3>Email Address</h3>
      <p>
        Primary company email address. Used for general correspondence and system communications.
      </p>

      <h3>Phone Number</h3>
      <p>
        Company's main phone number. Include country code for international compatibility:
      </p>
      <ul>
        <li>Format: +[country code] [number]</li>
        <li>Example: +1 (555) 123-4567</li>
      </ul>

      <h3>Notification Email (Requires Verification)</h3>
      <p>
        This is the email address that receives CRM system notifications such as:
      </p>
      <ul>
        <li>New lead alerts</li>
        <li>Drip campaign status updates</li>
        <li>Integration errors and warnings</li>
        <li>Daily/weekly summary reports</li>
        <li>User activity alerts</li>
      </ul>

      <div className="not-prose bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 my-8">
        <h4 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-3 flex items-center">
          <Mail className="w-5 h-5 mr-2" />
          AWS SES Email Verification Required
        </h4>
        <p className="text-sm text-red-800 dark:text-red-300 mb-3">
          Amazon Simple Email Service (SES) requires verification before you can receive notifications
          to this email address. This is a security measure to prevent spam and ensure email deliverability.
        </p>

        <h5 className="font-semibold text-red-900 dark:text-red-200 mb-2">How Email Verification Works:</h5>
        <ol className="text-sm text-red-700 dark:text-red-300 space-y-2 ml-4">
          <li>1. Enter your notification email address</li>
          <li>2. Click "Save Changes"</li>
          <li>3. Click the "Verify Email" button</li>
          <li>4. Check your inbox for AWS SES verification email</li>
          <li>5. Click the verification link in the email</li>
          <li>6. Return to CRM and click the refresh button</li>
          <li>7. Status will change to "Verified" ✅</li>
        </ol>
      </div>

      <h4>Verification Status Indicators:</h4>
      <div className="not-prose space-y-3 my-6">
        <div className="flex items-center space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-green-500 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Email Verified</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Notification email is verified and active. You will receive system notifications.
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-yellow-500 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Verification Pending</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Verification email sent. Check your inbox and click the link to verify.
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3 p-4 bg-white dark:bg-gray-800 border-l-4 border-red-500 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">Not Verified</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Email not verified. Click "Verify Email" to start the verification process.
            </p>
          </div>
        </div>
      </div>

      <h2>Address Information Section</h2>

      <h3>Complete Address Fields:</h3>
      <ul>
        <li><strong>Street Address</strong> - Full street address including building number</li>
        <li><strong>City</strong> - City or town name</li>
        <li><strong>State/Province</strong> - State, province, or region</li>
        <li><strong>ZIP/Postal Code</strong> - Postal code or ZIP code</li>
        <li><strong>Country</strong> - Country name</li>
      </ul>

      <p>
        Complete address information is used for:
      </p>
      <ul>
        <li>Invoice generation</li>
        <li>Legal compliance and documentation</li>
        <li>Customer correspondence</li>
        <li>Integration with shipping/billing systems</li>
      </ul>

      <h2>Step-by-Step Setup Guide</h2>

      <h3>Initial Configuration</h3>
      <ol>
        <li>Log in as admin user</li>
        <li>Navigate to <code>Settings → System Configuration → Client Information</code></li>
        <li>Fill in all required fields (marked with *):
          <ul>
            <li>Company Name</li>
            <li>Email Address</li>
          </ul>
        </li>
        <li>Fill in optional but recommended fields:
          <ul>
            <li>Website</li>
            <li>Phone Number</li>
            <li>Notification Email</li>
            <li>Timezone</li>
            <li>Complete Address</li>
            <li>Company Logo URL</li>
          </ul>
        </li>
        <li>Click "Save Changes"</li>
      </ol>

      <h3>Setting Up Notification Email</h3>
      <ol>
        <li>Enter your notification email in the "Notification Email" field</li>
        <li>Click "Save Changes" to save the email</li>
        <li>Click the "Verify Email" button that appears</li>
        <li>Check the inbox of the notification email</li>
        <li>Open the AWS SES verification email</li>
        <li>Click the verification link</li>
        <li>Return to CRM Client Information page</li>
        <li>Click the refresh button (circular arrow icon)</li>
        <li>Status should update to "Email Verified" with green checkmark</li>
      </ol>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>Didn't receive verification email?</strong>
        </p>
        <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
          <li>• Check spam/junk folder</li>
          <li>• Verify email address is correct</li>
          <li>• Click "Verify Email" again to resend</li>
          <li>• Contact support if issue persists</li>
        </ul>
      </div>

      <h2>Best Practices</h2>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              Client Information Checklist
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Keep information current</strong> - Update details whenever they change</li>
              <li>✓ <strong>Use professional email</strong> - Avoid personal emails for notifications</li>
              <li>✓ <strong>Verify notification email</strong> - Ensure you receive system alerts</li>
              <li>✓ <strong>Set correct timezone</strong> - Critical for scheduled tasks and reports</li>
              <li>✓ <strong>Upload high-quality logo</strong> - Professional branding matters</li>
              <li>✓ <strong>Complete all address fields</strong> - Required for invoicing and compliance</li>
              <li>✓ <strong>Review quarterly</strong> - Ensure information remains accurate</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Email verification link doesn't work?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p><strong>Possible causes:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Link may have expired (valid for 24 hours) - request new verification</li>
              <li>Link was already used - check if status shows "Verified"</li>
              <li>Copied link was truncated - click link directly from email</li>
              <li>Try incognito/private browser window</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Logo not displaying correctly?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Check the following:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>URL must be publicly accessible (not behind login)</li>
              <li>URL should end with image extension (.png, .jpg, .svg)</li>
              <li>Use HTTPS URLs for security</li>
              <li>Check if image loads in new browser tab</li>
              <li>Recommended size: 200x200px or larger</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Can't save changes - "Authentication required" error?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Solutions:</strong></p>
            <ol className="list-decimal list-inside space-y-1 mt-2">
              <li>Your session may have expired - refresh page and log in again</li>
              <li>Verify you're logged in as admin user</li>
              <li>Check browser console for errors</li>
              <li>Clear browser cache and cookies, then log in again</li>
            </ol>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Notification email changed - what happens to old verification?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p>
              When you change the notification email, the verification status automatically resets.
              You must verify the new email address following the same verification process.
              The old email will no longer receive notifications.
            </p>
          </div>
        </details>
      </div>

      <h2>Security Considerations</h2>

      <div className="not-prose bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 my-6">
        <p className="text-sm text-orange-800 dark:text-orange-300 mb-2">
          <strong>🔒 Security Tips:</strong>
        </p>
        <ul className="text-sm text-orange-700 dark:text-orange-300 space-y-1">
          <li>• Only share admin access with trusted team members</li>
          <li>• Use company email domains for notification emails</li>
          <li>• Regularly review who has admin access</li>
          <li>• Keep contact information current for security alerts</li>
          <li>• Don't use personal email addresses for business notifications</li>
        </ul>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/user-management"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Set Up User Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Create user accounts and assign roles for your team
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/drip-campaigns"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Configure Drip Campaigns</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set up automated email sequences with verified notification email
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Client Information Setup Complete!
            </h3>
            <p className="text-green-800 dark:text-green-300">
              Your organization's profile is now configured. Make sure to verify your notification
              email to receive important system alerts and updates. Keep this information current
              as it's used throughout the CRM for communications and reporting.
            </p>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientInformationDocs;
